import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Sparkles, Heart, MessageSquare, DoorOpen, FileCheck2, ArrowRight, User as UserIcon, Mail, Phone, ClipboardCheck, Compass, Users, Target, Rocket, TrendingUp, FileSearch, PresentationIcon, LineChart, ShieldCheck } from "lucide-react";
import RejectionFeedbackCard from "../../components/RejectionFeedbackCard";
import DebtSuitabilityTile from "../../components/debt/DebtSuitabilityTile";
import DashboardShell from "../../components/layout/DashboardShell";
import TrustScoreCard from "../../components/marketplace/TrustScoreCard";
import TrustBoostNudge from "../../components/startup/TrustBoostNudge";
import StartupDeepDDCard from "../../components/startup/StartupDeepDDCard";
import OnboardingHub from "../../components/startup/OnboardingHub";
import StartupOnboardingWizard from "../../components/startup/StartupOnboardingWizard";
import InvestorSignalPreview from "../../components/startup/InvestorSignalPreview";
import InvestorDigestPreviewCard from "../../components/startup/InvestorDigestPreviewCard";
import ProfileStrengthCard from "../../components/startup/ProfileStrengthCard";
import PeerBenchmarkCard from "../../components/startup/PeerBenchmarkCard";
import KycTrafficLight from "../../components/startup/KycTrafficLight";
import FounderUpdateFeed from "../../components/startup/FounderUpdateFeed";
import GuidedTour from "../../components/common/GuidedTour";
import { StatTile, ProgressBar, Card, Pill, Button, EmptyState } from "../../components/common/Primitives";
import { WelcomeHero, WarmStatTile, SoftSection } from "../../components/warm/WelcomeHero";
import { RevenueSpotlight, ServiceCardTrio } from "../../components/warm/RevenueSpotlight";
import api from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

const TOUR_STEPS = [
  {
    selector: "onboarding-hub",
    title: "Your journey, at a glance",
    body: "Six steps · about 40 minutes total. The green ring tracks your progress; the primary button always points to your next best action.",
  },
  {
    selector: "investor-signal-preview",
    title: "What investors actually see",
    body: "This mirrors your card in the Verified Directory, right now. Fields you fill land here immediately — so you can feel edits paying off.",
  },
  {
    selector: "tile-completeness",
    title: "Completeness — the fast metric",
    body: "Weighted 0-100 across 19 profile fields. This number always matches the Onboarding Hub's Profile step, so you never see conflicting scores.",
  },
  {
    selector: "section-recent-matches",
    title: "Recent matches surface here",
    body: "Once approved, matched investors appear on this list within seconds. Each match gets an AI-drafted reason under the hood.",
  },
  {
    selector: "startup-poc-card",
    title: "Your VenturaZ POC",
    body: "One human, one phone number. They shepherd your file through KYC, review and investor conversations. Message them anytime.",
  },
  {
    selector: "submit-review-btn",
    title: "Ship when you're ready",
    body: "This submits your file to VenturaZ review. Most files go visible within 48 hours after you tap this.",
  },
];

export default function StartupDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [matches, setMatches] = useState([]);
  const [interests, setInterests] = useState([]);
  const [queries, setQueries] = useState([]);
  const [dealRooms, setDealRooms] = useState([]);
  const [poc, setPoc] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const backfillPromptedRef = useRef(false);

  const load = async () => {
    try {
      const [m, mm, ii, qq, dd, p, wf] = await Promise.all([
        api.get("/startup/me"),
        api.get("/startup/matches"),
        api.get("/startup/interests"),
        api.get("/startup/queries"),
        api.get("/startup/deal-rooms"),
        api.get("/startup/my-poc"),
        api.get("/startup/diligence-workflow").catch(() => ({ data: null })),
      ]);
      setMe(m.data);
      setMatches(mm.data || []);
      setInterests(ii.data || []);
      setQueries(qq.data || []);
      setDealRooms(dd.data || []);
      setPoc(p.data?.assigned ? p.data.poc : null);
      setWorkflow(wf.data || null);
      // Sector Backfill Nudge — one-shot auto-open of the onboarding wizard
      // if the founder never finished the basics. Guarded via useRef so a
      // subsequent close→reload cycle can't retrigger it (else the modal
      // becomes un-dismissible until they finish, but the persistent banner
      // + Complete-now CTA remain the re-entry path).
      const s = m.data?.startup || {};
      if (!backfillPromptedRef.current
          && s.startup_id && (!s.sector || !s.startup_name)) {
        backfillPromptedRef.current = true;
        setWelcomeOpen(true);
      }
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/startup/submit-for-review");
      toast.success("Submitted for review.");
      load();
    } catch (e) { toast.error("Submission failed"); }
  };

  const status = me?.startup?.status || "Draft";
  const kyc = me?.startup?.kyc_status || "Not Submitted";
  const funding = me?.funding && Object.keys(me.funding).length > 0 ? "Draft/Submitted" : "Empty";

  // Only show Submit-for-Review when:
  //   • profile has never been submitted (Draft), OR
  //   • last review was rejected, OR
  //   • founder has edited the profile since the last submission.
  const submittedAt = me?.startup?.submitted_at;
  const updatedAt = me?.startup?.updated_at;
  const hasPendingChanges = !!(submittedAt && updatedAt && new Date(updatedAt) > new Date(submittedAt));
  const canSubmit = status === "Draft" || status === "Rejected" || hasPendingChanges;

  return (
    <DashboardShell
      subtitle={`Welcome, ${user?.name || user?.email}`}
      title="Founder Cockpit"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTourOpen(true)}
            className="text-xs px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5"
            data-testid="start-tour-btn"
          >
            <Compass size={12} /> Take the tour
          </button>
          <Button onClick={submit} data-testid="submit-review-btn" disabled={!canSubmit} title={canSubmit ? "Send your profile for VenturaZ review" : `Already ${status}. Edit your profile to unlock re-submission.`} className={!canSubmit ? "!opacity-60 !cursor-not-allowed" : ""}>
            {status === "Draft" || status === "Rejected" ? "Submit for Review" : hasPendingChanges ? "Re-submit for Review" : "Review pending"}
          </Button>
        </div>
      }
    >
      <Toaster richColors position="top-right" />

      <DebtSuitabilityTile />

      <StartupOnboardingWizard
        open={welcomeOpen}
        onClose={() => {
          setWelcomeOpen(false);
          // Auto-launch the guided tour the first time a founder sees the welcome
          // wizard, unless they've already seen the tour. Wait until the regulatory
          // Exchange Declaration is acknowledged, otherwise the tour spotlight
          // fights that modal for pointer events.
          try {
            if (localStorage.getItem("venturaz.tour_seen")) return;
            const ackOk = () => localStorage.getItem("venturaz_exchange_ack_v1") === "1";
            if (ackOk()) {
              setTimeout(() => setTourOpen(true), 400);
            } else {
              // Poll briefly until the founder acks; give up after 60s.
              let tries = 0;
              const t = setInterval(() => {
                tries += 1;
                if (ackOk()) { clearInterval(t); setTourOpen(true); }
                else if (tries > 60) { clearInterval(t); }
              }, 1000);
            }
          } catch (e) { /* ignore */ }
          load();
        }}
      />
      <GuidedTour open={tourOpen} steps={TOUR_STEPS} onClose={() => setTourOpen(false)} />

      {me?.startup && (!me.startup.sector || !me.startup.startup_name) && (
        <div
          className="mb-4 border border-amber-300 bg-amber-50/70 rounded-md p-3 flex items-start justify-between gap-3"
          data-testid="startup-sector-backfill-banner"
        >
          <div className="flex items-start gap-2">
            <Compass size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading text-amber-900 text-sm">Finish your basics to appear in the Verified Directory</div>
              <div className="text-xs text-amber-800 mt-0.5">
                {!me.startup.startup_name && "Startup name is empty. "}
                {!me.startup.sector && "Sector is missing. "}
                Investors can&apos;t discover you until these are filled.
              </div>
            </div>
          </div>
          <button
            onClick={() => setWelcomeOpen(true)}
            className="text-xs px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-heading flex-shrink-0"
            data-testid="startup-sector-backfill-cta"
          >
            Complete now →
          </button>
        </div>
      )}

      <WelcomeHero
        name={user?.name || user?.email}
        eyebrow="FOUNDER COCKPIT"
        subhead={
          status === "Approved"
            ? "You're live in the Verified Directory. Here's who's watching."
            : "Let's get your file investor-ready — a few small steps and you're on stage."
        }
        badge={
          <div className="flex flex-col items-end gap-2">
            <Pill tone="gold">{status}</Pill>
            <Pill tone={kyc === "Approved" ? "success" : "warn"}>KYC · {kyc}</Pill>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <WarmStatTile
            index={0}
            label="Profile Completeness"
            value={`${user?.profile_completeness ?? 10}%`}
            icon={Target}
            hint="Weighted across 19 fields"
            href="/startup/profile"
            testId="tile-completeness"
          />
          <WarmStatTile
            index={1}
            label="Matched Investors"
            value={matches.length}
            icon={Users}
            hint={matches.length > 0 ? "See your top matches" : "Complete your profile"}
            href="/startup/matches"
            testId="tile-matches"
          />
          <WarmStatTile
            index={2}
            label="Investor Interest"
            value={interests.length}
            icon={Heart}
            hint={interests.length > 0 ? "Hearts arriving" : "Awaiting first spark"}
            href="/startup/investor-interest"
            testId="tile-interests"
          />
          <WarmStatTile
            index={3}
            label="Deal Rooms"
            value={dealRooms.length}
            icon={DoorOpen}
            hint={dealRooms.length > 0 ? "Active conversations" : "None yet"}
            href="/startup/deal-rooms"
            testId="tile-dealrooms"
          />
        </div>
      </WelcomeHero>

      <SoftSection delay={0.05}>
        <RevenueSpotlight
          eyebrow="ACCELERATE YOUR RAISE"
          title="Meet investors who've closed cheques in your sector this quarter."
          subtitle="VenturaZ Premium moves your file to the top of the review queue and unlocks warm intros to the institutional tier of investors on the platform."
          bullets={[
            "Priority profile review by our analysts",
            "Featured listing in the investor Directory",
            "Unlimited investor introductions",
            "Advisor-led deal room with e-sign",
          ]}
          primary={{ href: "/pricing", label: "See Premium Plans", price: "Starting ₹49,000 / year" }}
          secondary={{ href: "/startup/premium", label: "How Premium works →" }}
        />
      </SoftSection>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <OnboardingHub
            onLoaded={(data) => {
              if (data && data.welcome_seen === false) setWelcomeOpen(true);
            }}
          />
        </div>
        <div className="lg:col-span-1">
          <InvestorSignalPreview />
        </div>
      </div>

      <InvestorDigestPreviewCard />

      <ProfileStrengthCard />

      <SoftSection delay={0.1}>
        <ServiceCardTrio
          eyebrow="DILIGENCE SERVICES"
          title="Investor-ready deliverables, on demand"
          items={[
            {
              id: "deep-dd",
              icon: FileSearch,
              title: "Deep Due Diligence Report",
              description: "Financial forensics + market validation + team + governance signals — the exact report investors want.",
              cta: "Get a report",
              href: "/startup/diligence",
              priceLabel: "From ₹75,000",
            },
            {
              id: "pitch-review",
              icon: PresentationIcon,
              title: "Pitch Deck Review",
              description: "45-minute call with a senior analyst + written feedback. Sharpen your narrative before the raise.",
              cta: "Book a review",
              href: "/startup/diligence",
              priceLabel: "₹15,000",
            },
            {
              id: "market-sizing",
              icon: LineChart,
              title: "Market Sizing Report",
              description: "TAM · SAM · SOM built from primary data. Numbers investors trust because they can trace them.",
              cta: "Order sizing",
              href: "/startup/diligence",
              priceLabel: "From ₹25,000",
            },
          ]}
        />
      </SoftSection>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="secondary-stats-row">
        <StatTile label="Status" value={<Pill tone="gold">{status}</Pill>} testId="tile-status" />
        <StatTile label="KYC" value={<Pill tone={kyc === "Approved" ? "success" : "warn"}>{kyc}</Pill>} testId="tile-kyc" />
        <StatTile label="Funding Profile" value={funding} testId="tile-funding" />
        <StatTile label="Open Queries" value={queries.filter(q => q.status === "Startup Response Pending").length} testId="tile-queries" />
      </div>

      <div className="mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <TrustScoreCard />
            <TrustBoostNudge />
            <StartupDeepDDCard />
          </div>
          <div className="lg:col-span-1 space-y-6">
            <KycTrafficLight />
            <PeerBenchmarkCard />
          </div>
        </div>
      </div>

      <div className="mb-8">
        <FounderUpdateFeed />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {workflow && (
            <Card testId="section-diligence-workflow">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="overline flex items-center gap-1"><ClipboardCheck size={12} /> DILIGENCE WORKFLOW</div>
                  <div className="font-heading text-ink text-lg mt-1" data-testid="startup-workflow-label">{workflow.state_label}</div>
                </div>
                <Pill tone="gold" testId="startup-workflow-pill">Live</Pill>
              </div>
              {workflow.updated_at && (
                <div className="text-[11px] text-ink-muted font-mono">
                  Last updated {workflow.updated_at.slice(0, 16).replace("T", " ")}
                </div>
              )}
              {workflow.history?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="startup-workflow-history">
                  {workflow.history.slice(-5).map((h, i) => (
                    <Pill key={i} tone="muted">{(h.state || "").replace(/_/g, " ")}</Pill>
                  ))}
                </div>
              )}
            </Card>
          )}
          <RejectionFeedbackCard />
          <Card testId="section-next-actions">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Suggested Next Actions</div>
                <div className="font-heading text-ink text-lg mt-1">Get investor-ready</div>
              </div>
              <Sparkles className="text-gold" size={18} />
            </div>
            <div className="space-y-3">
              <NextAction to="/startup/profile" label="Complete company profile" hint="Legal entity, sector, stage, founders" />
              <NextAction to="/startup/kyc" label="Submit KYC" hint="Ensures visibility to institutional investors" />
              <NextAction to="/startup/funding-profile" label="Fill funding profile" hint="Ask amount, valuation, use of funds" />
              <NextAction to="/startup/documents" label="Upload pitch deck & financials" hint="Documents unlock deeper investor interest" />
            </div>
          </Card>

          <Card testId="section-recent-matches">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Recent Matches</div>
              <Link to="/startup/matches" className="text-gold text-sm font-heading">See all →</Link>
            </div>
            {matches.length === 0 ? (
              <EmptyState title="No investor matches yet" hint="Complete your profile and funding requirement — matches show up as investors get approved." />
            ) : (
              <div className="space-y-2">
                {matches.slice(0, 5).map((m) => (
                  <div key={m.investor_id} className="flex items-center justify-between border border-white/10 p-3">
                    <div>
                      <div className="text-ink font-heading">{m.investor_name}</div>
                      <div className="text-xs text-ink-muted font-mono uppercase tracking-wider">{m.investor_type} · Ticket ₹{(m.ticket_size_min || 0) / 1e5}L-₹{(m.ticket_size_max || 0) / 1e7}Cr</div>
                    </div>
                    <div className="match-badge px-2 py-1 text-[11px] font-mono">{m.match_score}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {poc ? (
            <Card testId="startup-poc-card">
              <div className="overline mb-3">Your VenturaZ POC</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#007453] to-[#1795BD] flex items-center justify-center text-white font-heading text-lg">
                  {(poc.name || poc.email).split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-ink text-sm truncate">{poc.name || poc.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{poc.role}</div>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <a href={`mailto:${poc.email}`} className="flex items-center gap-2 text-ink-soft hover:text-[#8A6600]" data-testid="startup-poc-email">
                  <Mail size={14} className="text-[#B58900]" /> {poc.email}
                </a>
                {poc.mobile && (
                  <a href={`tel:${poc.mobile}`} className="flex items-center gap-2 text-ink-soft hover:text-[#8A6600]" data-testid="startup-poc-phone">
                    <Phone size={14} className="text-[#B58900]" /> {poc.mobile}
                  </a>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-navy/10 text-xs text-ink-muted">
                Your dedicated POC for KYC, profile review and getting funder-ready.
              </div>
            </Card>
          ) : (
            <Card testId="startup-poc-empty">
              <div className="overline mb-3">Your VenturaZ POC</div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-navy/[0.06] flex items-center justify-center text-ink-muted">
                  <UserIcon size={20} />
                </div>
                <div className="text-ink-muted text-sm">Awaiting assignment — a VenturaZ POC will be assigned to you within 24h.</div>
              </div>
            </Card>
          )}

          <Card testId="section-interests">
            <div className="overline mb-3">Investor Interest</div>
            {interests.length === 0 ? (
              <div className="text-ink-muted text-sm">No investor interest yet.</div>
            ) : (
              <div className="space-y-2">
                {interests.slice(0, 5).map((i) => (
                  <div key={i.interest_id} className="border border-white/10 p-3">
                    <div className="text-ink text-sm font-heading">{i.investor_name}</div>
                    <div className="text-xs text-ink-muted font-mono uppercase mt-1">{i.status}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card testId="section-open-queries">
            <div className="overline mb-3">Open Queries</div>
            {queries.length === 0 ? (
              <div className="text-ink-muted text-sm">No queries received yet.</div>
            ) : (
              <div className="space-y-2">
                {queries.slice(0, 3).map((q) => (
                  <Link to="/startup/queries" key={q.query_id} className="block border border-white/10 p-3 hover:border-gold/40 transition-colors">
                    <div className="text-ink text-sm font-heading flex items-center justify-between">
                      Query · {q.questions?.length || 0} questions
                      <ArrowRight size={14} className="text-ink-faint" />
                    </div>
                    <div className="text-xs text-ink-muted font-mono uppercase mt-1">{q.status}</div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardShell>
  );
}

function NextAction({ to, label, hint }) {
  return (
    <Link to={to} className="flex items-center justify-between border border-white/10 p-3 hover:border-gold/40 transition-colors">
      <div>
        <div className="text-ink font-heading text-sm">{label}</div>
        <div className="text-xs text-ink-muted mt-0.5">{hint}</div>
      </div>
      <ArrowRight size={14} className="text-gold" />
    </Link>
  );
}
